/**
 * @module components/Checkout/formReducer
 * @description Form validation and state management reducer for checkout form.
 * Handles field validation, error state, and provides validation summary.
 */

import {
  FormState,
  FormAction,
  FormActionType,
  FormErrors,
  FormFieldName,
  ValidationRuleType,
} from 'src/types/forms';

/**
 * Validation RegEx
 */
const websiteRegex =
  /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;
const nonStrictIntlZip = /^[a-zA-Z0-9][a-zA-Z0-9\- ]{0,10}[a-zA-Z0-9]$/;

const requiredMsg = 'Required';

/**
 * @func required
 * @summary Determines if given value exists if we have a "required field" message (we do)
 * @param val - Form value
 * @returns Error message or false if valid
 */
const required = (val: string | number | undefined): string | false =>
  !val && requiredMsg;

/**
 * @func tooShort
 * @summary Determines if a given string is too short (length <= `min`)
 * @param val - String to check
 * @param min - Minimum length to allow
 * @returns Error message or false if valid
 */
const tooShort = (
  val: string | number | undefined,
  min: number = 2,
): string | false => {
  if (val && typeof val === 'string' && val.length <= min) {
    return 'Too short';
  }
  return false;
};

/**
 * @func getErrorMessage
 * @summary Returns error message, or false if no error
 * @param field - Field name
 * @param val - Field value
 * @returns Error message if invalid field, false otherwise
 */
export const getErrorMessage = (
  field: string,
  val: string | number | undefined,
): string | false => {
  switch (field) {
    /* Required */
    case FormFieldName.NAME:
      return required(val) || tooShort(val);
    case FormFieldName.COMPANY:
      return required(val) || tooShort(val);
    case FormFieldName.COUNTRY:
      return required(val);
    case FormFieldName.COMPANY_ID:
      return required(val) || tooShort(val);
    /* Optional */
    case FormFieldName.PROVINCE:
      return tooShort(val, 1);
    case FormFieldName.ZIP:
      return (
        (val &&
          typeof val === 'string' &&
          !nonStrictIntlZip.test(val) &&
          'Invalid zip') ||
        false
      );
    case FormFieldName.WEBSITE:
      return (
        (val &&
          typeof val === 'string' &&
          !websiteRegex.test(val) &&
          'Invalid url') ||
        false
      );
    case FormFieldName.STREET2:
      return false;
    case FormFieldName.REFERENCE:
      return false;
    case FormFieldName.NOTE:
      return false;
    default:
      return tooShort(val);
  }
};

/**
 * @func summaryText
 * @summary Generate summary text for invalid fields (below "submit" button)
 * @param arr - Array of field names
 * @param rule - Validation rule type
 * @returns Error summary text
 */
const summaryText = (arr: string[], rule: ValidationRuleType): string => {
  const a = [...arr];
  const last = a.pop();
  const fields = `${a.join(', ')}${
    a.length ? `${a.length === 1 ? '' : ','} and ` : ''
  }${last ?? ''}`;
  const msg = `${fields} ${arr.length === 1 ? 'is' : 'are'} ${rule}.`;
  return msg.charAt(0).toUpperCase() + msg.slice(1);
};

/**
 * @func getErrorSummary
 * @summary Generate "summary" text for missing/invalid fields
 * @param errors - Map of errors
 * @returns Array of error messages or null if no errors
 */
const getErrorSummary = (errors: FormErrors): string[] | null => {
  const missing = Object.entries(errors)
    .map(([k, v]) => v && v === requiredMsg && k)
    .filter(Boolean) as string[];
  const invalid = Object.entries(errors)
    .map(([k, v]) => v && v !== requiredMsg && k)
    .filter(Boolean) as string[];

  if (!missing.length && !invalid.length) return null;

  return [
    ...(missing.length
      ? [summaryText(missing, ValidationRuleType.REQUIRED)]
      : []),
    ...(invalid.length
      ? [summaryText(invalid, ValidationRuleType.INVALID)]
      : []),
  ];
};

/**
 * @func setValues
 * @summary Reducer helper to combine field values w/ existing state/payload
 * @param values - Current form values
 * @param payload - New values to merge
 * @returns Combined object with values + payload
 */
const setValues = (
  values: FormState['values'],
  payload: Record<string, string>,
): FormState['values'] => ({
  ...values,
  ...payload,
});

/**
 * @func setErrors
 * @summary Reducer helper to combine field errors w/ existing state/payload
 * @param errors - Current form errors
 * @param payload - New errors to merge
 * @returns Combined object with errors + payload
 */
const setErrors = (
  errors: FormErrors,
  payload: Record<string, string>,
): FormErrors => ({
  ...errors,
  ...payload,
});

/**
 * @func unsetErrors
 * @summary Remove given field/payload from error object
 * @param errors - Current form errors
 * @param field - Field name to unset
 * @returns Updated errors object with field error removed
 */
const unsetErrors = (errors: FormErrors, field: string): FormErrors => ({
  ...errors,
  [field]: false,
});

/**
 * @func validate
 * @summary Validate given field
 * @param state - React reducer state (via `useReducer`)
 * @param field - Field name to validate
 * @returns Revised errors with error messages (will unset error if field corrected)
 */
const validate = (state: FormState, field: string): FormErrors => {
  const msg = getErrorMessage(field, state.values[field]);
  return msg
    ? setErrors(state.errors, { [field]: msg })
    : unsetErrors(state.errors, field);
};

/**
 * @func reset
 * @summary Reset form state
 * @param initialState - Add extra values to initial/reset form state
 * @returns Initial/reset form state
 */
export const reset = (
  initialState: Partial<FormState> = {},
): FormState => ({
  values: initialState.values || {},
  errors: initialState.errors || {},
});

/**
 * @func processErrors
 * @summary Create new reducer state with error object + error summary
 * @param state - Current reducer state
 * @param newErrors - Map of field errors
 * @returns Validation state w/ errors
 */
const processErrors = (state: FormState, newErrors: FormErrors): FormState => ({
  ...state,
  errorSummary: getErrorSummary(newErrors),
  errors: newErrors,
});

/**
 * @func formReducer
 * @summary Perform all form lifecycle actions (init/reset/setValue) + validation, add errors if any
 * @param state - Reducer state
 * @param action - Which action to run when reducer is invoked
 * @returns Updated reducer state w/ errors
 */
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case FormActionType.INIT: {
      const newState = { ...state, ...action.payload };
      return processErrors(newState, {});
    }
    case FormActionType.RESET: {
      const blankState: FormState = { values: {}, errors: {} };
      const newState = { ...blankState, ...(action.payload || {}) };
      return processErrors(newState, {});
    }
    case FormActionType.SET_VALUE: {
      const field = Object.keys(action.payload)?.[0];
      const newState = { ...state, values: setValues(state.values, action.payload) };
      return !state.errors[field] && state.errors[field] !== false
        ? newState
        : processErrors(newState, validate(newState, field));
    }
    case FormActionType.SET_ERROR:
      return processErrors(state, setErrors(state.errors, action.payload));
    case FormActionType.UNSET_ERROR:
      return processErrors(state, unsetErrors(state.errors, action.payload));
    case FormActionType.VALIDATE:
      return processErrors(state, validate(state, action.payload));
    default:
      return state;
  }
}

export default formReducer;
